import {
  MAIN_SIDENAV_WORKSPACE_SECTIONS,
  PAGE_SECTIONS,
} from "@/components/Layouts/pageSections";
import { TOOL_TYPES, TYPE_CONFIG } from "@/components/Layouts/blockTypes";

export type ResolvedSidenav =
  // Tool pages draw their own (ToolLayout): its sections are the tool's tabs,
  // which only the page knows.
  | { kind: "tool" }
  | { kind: "inbox"; inboxId: string }
  // Without an id, the current workspace (Manage workspace); with one, a
  // workspace opened from Settings › Workspaces.
  | { kind: "workspace"; workspaceId?: string }
  | { kind: "settings" }
  | { kind: "apps" }
  | { kind: "main" };

// Tool pages, and the template pages that use the same layout.
const TOOL_PAGE_PREFIXES = [
  ...TOOL_TYPES.map((type) => TYPE_CONFIG[type].href("")),
  "/templates/",
];

const APPS_PREFIXES = ["/apps/f", "/apps/c", "/apps/d"];

// Which sidenav a dashboard route shows. `allowedWorkspaceSections` are the
// Manage workspace sections the user may open: one they may not falls back to
// a Manage workspace section on the page, so it keeps that sidenav too.
export const resolveSidenav = (
  pathname: string,
  params: Pick<URLSearchParams, "get"> | null,
  allowedWorkspaceSections?: readonly string[]
): ResolvedSidenav => {
  if (TOOL_PAGE_PREFIXES.some((prefix) => pathname.startsWith(prefix)))
    return { kind: "tool" };

  const inboxId =
    pathname.match(/^\/inbox\/([^/]+)$/)?.[1] ??
    // A case or task opened from an inbox keeps the inbox's sidenav.
    (/^\/(cases|tasks)\/[^/]+$/.test(pathname)
      ? params?.get("inboxId")
      : undefined);
  if (inboxId) return { kind: "inbox", inboxId };

  const tab = params?.get("tab");
  const opensMainSection = MAIN_SIDENAV_WORKSPACE_SECTIONS.some(
    (id) =>
      id === tab &&
      (!allowedWorkspaceSections || allowedWorkspaceSections.includes(id))
  );
  if (pathname === PAGE_SECTIONS.workspace.path && !opensMainSection)
    return { kind: "workspace" };

  const workspaceId = pathname.match(
    /^\/settings\/administration\/workspaces\/([^/]+)$/
  )?.[1];
  if (workspaceId) return { kind: "workspace", workspaceId };

  if (pathname.startsWith("/settings")) return { kind: "settings" };
  if (APPS_PREFIXES.some((prefix) => pathname.startsWith(prefix)))
    return { kind: "apps" };
  return { kind: "main" };
};
